import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { aiChatPanel } from '@elementary/common-ui/atoms';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSnackbar from 'shared/hooks/useSnackbar';

const AiChatConversationPage = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();
  const envHint = searchParams.get('env');
  const selectedEnvId = useSelectedEnv();
  const navigate = useNavigate();
  const hasEnvPermission = useEnvPermission();
  const [panelState, setPanelState] = useRecoilState(aiChatPanel);
  const { openSnackbar } = useSnackbar();
  const initializedRef = useRef(false);
  const wasFullWidthRef = useRef(false);

  const defaultRedirect =
    hasEnvPermission('VIEW_ASSETS') && hasEnvPermission('VIEW_TESTS')
      ? 'report/dashboard'
      : 'catalog';

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    setPanelState((prev) => ({
      ...prev,
      isExpanded: true,
      isFullWidth: true,
      ...(threadId && {
        initialConfig: {
          initialThreadId: threadId,
        },
      }),
    }));
  }, [threadId, envHint, setPanelState]);

  useEffect(() => {
    if (!panelState.threadLoadError) return;
    setPanelState((prev) => ({ ...prev, threadLoadError: false }));
    openSnackbar({
      variant: 'error',
      message:
        'This conversation is not available. It may belong to another user.',
    });
    navigate('/ai-chat', { replace: true });
  }, [panelState.threadLoadError, setPanelState, openSnackbar, navigate]);

  useEffect(() => {
    if (panelState.isExpanded && panelState.isFullWidth) {
      wasFullWidthRef.current = true;
    }
    if (!wasFullWidthRef.current) return;
    if (!panelState.isExpanded || !panelState.isFullWidth) {
      const envId = envHint || selectedEnvId;
      const base = envId ? `/${envId}` : '';
      navigate(`${base}/${defaultRedirect}`, { replace: true });
    }
  }, [
    panelState.isExpanded,
    panelState.isFullWidth,
    envHint,
    selectedEnvId,
    navigate,
    defaultRedirect,
  ]);

  useEffect(
    () => () => {
      setPanelState((prev) => ({ ...prev, isFullWidth: false }));
    },
    [setPanelState],
  );

  return null;
};

export default AiChatConversationPage;
